import { useState, useEffect } from 'react';
import { enterpriseAPI, infraAPI, securityAPI, stealthAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Loader2, Shield, Zap, Globe, CheckCircle, XCircle, AlertTriangle, RefreshCw,
  Server, Activity, Lock,
} from 'lucide-react';
import { toast } from 'sonner';

export default function EnterpriseDashboardPage() {
  const [data, setData] = useState(null);
  const [infra, setInfra] = useState(null);
  const [security, setSecurity] = useState(null);
  const [modes, setModes] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [dRes, iRes, sRes, mRes] = await Promise.all([
        enterpriseAPI.dashboard().catch(() => ({ data: null })),
        infraAPI.dashboard().catch(() => ({ data: null })),
        securityAPI.abuseDetect().catch(() => ({ data: null })),
        stealthAPI.modes().catch(() => ({ data: [] })),
      ]);
      if (dRes.data) setData(dRes.data);
      if (iRes.data) setInfra(iRes.data);
      if (sRes.data) setSecurity(sRes.data);
      if (mRes.data) setModes(mRes.data);
    } catch (e) {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>;

  const d = data?.domains || {};
  const healthDist = data?.health_distribution || {};

  return (
    <div className="space-y-6" data-testid="enterprise-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold">Enterprise Dashboard</h1>
          <p className="text-muted-foreground mt-1">AI Email Infrastructure Hub</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => { setLoading(true); load(); }}><RefreshCw className="w-4 h-4 mr-1" /> Yenile</Button>
      </div>

      {/* Top KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <div className="rounded-lg bg-card p-4 text-center">
          <Globe className="w-5 h-5 mx-auto text-blue-500 mb-1" />
          <p className="text-2xl font-bold">{d.total || 0}</p>
          <p className="text-xs text-muted-foreground">Toplam Domain</p>
        </div>
        <div className="rounded-lg bg-card p-4 text-center">
          <CheckCircle className="w-5 h-5 mx-auto text-green-500 mb-1" />
          <p className="text-2xl font-bold">{d.active || 0}</p>
          <p className="text-xs text-muted-foreground">Aktif Domain</p>
        </div>
        <div className="rounded-lg bg-card p-4 text-center">
          <Zap className="w-5 h-5 mx-auto text-amber-500 mb-1" />
          <p className="text-2xl font-bold">{(d.total_capacity || 0).toLocaleString()}</p>
          <p className="text-xs text-muted-foreground">Gunluk Kapasite</p>
        </div>
        <div className="rounded-lg bg-card p-4 text-center">
          <Activity className="w-5 h-5 mx-auto text-cyan-500 mb-1" />
          <p className="text-2xl font-bold">{(d.total_sent_today || 0).toLocaleString()}</p>
          <p className="text-xs text-muted-foreground">Bugun Gonderildi</p>
        </div>
        <div className="rounded-lg bg-card p-4 text-center">
          <Shield className="w-5 h-5 mx-auto text-purple-500 mb-1" />
          <p className="text-2xl font-bold">{data?.global_suppression || 0}</p>
          <p className="text-xs text-muted-foreground">Suppressed</p>
        </div>
        <div className="rounded-lg bg-card p-4 text-center">
          <Server className="w-5 h-5 mx-auto text-orange-500 mb-1" />
          <p className="text-2xl font-bold">{infra?.active_nodes || 0}</p>
          <p className="text-xs text-muted-foreground">Sending Nodes</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Health Map */}
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base">Domain Health Dagilimi</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-3 text-center">
              <div className="p-3 rounded-lg bg-green-500/10">
                <p className="text-xl font-bold text-green-500">{healthDist.healthy || 0}</p>
                <p className="text-[10px] text-muted-foreground">Saglikli</p>
              </div>
              <div className="p-3 rounded-lg bg-amber-500/10">
                <p className="text-xl font-bold text-amber-500">{healthDist.warning || 0}</p>
                <p className="text-[10px] text-muted-foreground">Uyari</p>
              </div>
              <div className="p-3 rounded-lg bg-red-500/10">
                <p className="text-xl font-bold text-red-500">{healthDist.danger || 0}</p>
                <p className="text-[10px] text-muted-foreground">Tehlike</p>
              </div>
              <div className="p-3 rounded-lg bg-gray-500/10">
                <p className="text-xl font-bold text-gray-400">{healthDist.paused || 0}</p>
                <p className="text-[10px] text-muted-foreground">Durduruldu</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Security Alerts */}
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Lock className="w-4 h-4" /> Guvenlik</CardTitle></CardHeader>
          <CardContent>
            {security?.alert_count > 0 ? (
              <div className="space-y-2">
                {security.alerts.map((a, i) => (
                  <div key={i} className="flex items-center gap-2 p-2 rounded bg-red-500/10 text-sm">
                    <AlertTriangle className="w-4 h-4 text-red-400" />
                    <span className="text-red-400">{a.message}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4">
                <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Guvenlik tehdidi yok</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Stealth Modes */}
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base">Gonderim Modlari</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {modes.map((m, i) => (
                <div key={i} className="flex items-center justify-between py-1.5">
                  <div>
                    <span className="text-sm font-medium capitalize">{m.mode?.replace('_', ' ')}</span>
                    <p className="text-[10px] text-muted-foreground">{m.description}</p>
                  </div>
                  <span className="text-xs text-muted-foreground">{m.delay_min}-{m.delay_max}s</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Infra Nodes */}
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Server className="w-4 h-4" /> Sending Nodes</CardTitle></CardHeader>
          <CardContent>
            {infra?.nodes?.length > 0 ? (
              <div className="space-y-2">
                {infra.nodes.map((n, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${n.status === 'active' ? 'bg-green-500' : 'bg-red-500'}`} />
                      <span className="text-sm font-mono">{n.ip}</span>
                      <span className="text-xs text-muted-foreground">{n.hostname}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">{n.sent}/{n.limit} | {n.domains} domain</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">Henuz node eklenmedi</p>
            )}
          </CardContent>
        </Card>

        {/* Domain List */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3"><CardTitle className="text-base">Domain Durumu</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-1.5 max-h-[300px] overflow-auto">
              {data?.domain_list?.map((d, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-muted/20">
                  <div className="flex items-center gap-3">
                    <span className={`text-sm font-bold ${d.health >= 70 ? 'text-green-500' : d.health >= 50 ? 'text-amber-500' : 'text-red-500'}`}>{d.health}</span>
                    <span className="text-sm">{d.domain}</span>
                    <Badge variant="outline" className="text-[10px]">{d.status}</Badge>
                    {d.created_from === 'trafficos' && <Badge className="text-[10px] bg-purple-500/20 text-purple-400">TrafficOS</Badge>}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span>Day {d.warmup_day}</span>
                    <span>{d.sent_today}/{d.daily_limit}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Audit Logs */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3"><CardTitle className="text-base">Son Audit Loglari</CardTitle></CardHeader>
          <CardContent>
            {data?.recent_audits?.length > 0 ? (
              <div className="space-y-1">
                {data.recent_audits.map((l, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5 px-2 text-sm">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[10px]">{l.action}</Badge>
                      <span>{l.domain}</span>
                      <span className="text-xs text-muted-foreground">{l.details}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">{l.created_at ? new Date(l.created_at).toLocaleString('tr-TR') : ''}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">Henuz audit log yok</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
